import {
  TenantConfig,
  TenantPolicies,
  TenantPolicySection,
  TenantRefundPolicy,
  TenantRulesPolicy,
} from './tenant.model';
import { safeSupportEmail, supportMailto } from './tenant-support-email';

export const OFFICIAL_FIBA_RULES_URL = 'https://about.fiba.basketball/en/services/resource-hub/downloads';
export const POLICY_LIMITS = Object.freeze({
  bytes: 16 * 1024,
  introduction: 2000,
  sections: 16,
  heading: 120,
  body: 3000,
  items: 12,
  itemText: 750,
  subitems: 6,
  subitem: 750,
});

export type TenantPolicyMode = 'legacy' | 'configured' | 'unavailable';

export interface TenantPolicyView<T extends TenantRulesPolicy | TenantRefundPolicy> {
  mode: TenantPolicyMode;
  document: T;
  unavailableMessage: string;
  supportEmail: string;
  supportMailto: string;
}

function compact(value: unknown): string {
  return String(value ?? '').trim();
}

function section(
  heading: string,
  items: Array<string | { text: string; subitems: string[] }> = [],
  body = '',
  kind: 'standard' | 'notice' = 'standard',
): TenantPolicySection {
  return {
    heading,
    body,
    kind,
    items: items.map((item) => typeof item === 'string' ? { text: item, subitems: [] } : item),
  };
}

export function emptyRulesPolicy(configured = false): TenantRulesPolicy {
  return { configured, introduction: '', show_fiba_resource: false, sections: [] };
}

export function emptyRefundPolicy(configured = false): TenantRefundPolicy {
  return { configured, introduction: '', sections: [] };
}

export function emptyTenantPolicies(): TenantPolicies {
  return { rules: emptyRulesPolicy(), refund: emptyRefundPolicy() };
}

export function worldLeagueRulesPolicy(leagueName = 'World League', supportEmail = 'info@worldleague.ca'): TenantRulesPolicy {
  const supportContact = compact(supportEmail) || 'league administration';
  return {
    configured: false,
    introduction: `${leagueName} follows the foundational framework of FIBA basketball rules while incorporating league-specific modifications. For a comprehensive understanding of the FIBA basketball rules, please refer to the official document.`,
    show_fiba_resource: true,
    sections: [
      section('GAME LENGTH', [
        'Two 23-minute halves.',
        { text: 'During regular season:', subitems: ['Final two (2) minutes of the second half enters "stop time" if the point difference between the two teams is 10 points or less.'] },
        { text: 'During playoff Games:', subitems: ['Final two (2) minutes of the second half enters "stop time" if the point difference between the two teams is 15 points or less.'] },
        'Shot clock is twenty-four (24) Seconds.',
        'If the shot clock malfunctions and is not operational, the referee will manually count down the 24-second shot clock.',
      ]),
      section('OVERTIME', [
        'Overtime periods are set for a duration of two (2) minutes each.',
        'In the final 30 seconds of each overtime period, it enters "STOP TIME".',
        { text: 'During the regular season:', subitems: ['If the game remains tied after the first overtime period, a second overtime is played.', 'In the second overtime, the first team to score a point wins the game.'] },
        { text: 'During playoff games:', subitems: ['Overtime periods will continue to be played as needed until a winner is determined, without any limit on the number of overtime periods.'] },
      ]),
      section('TIME-OUT', [
        'Each team is granted a total of three (3) timeouts per game.',
        'Only two (2) timeouts can be use per half.',
        'Unused timeouts from regular game time cannot be carried over into overtime.',
        'During overtime periods, each team is entitled to one (1) timeout.',
      ]),
      section('PENALTY (BONUS)', ['When a team Accumulates eight (8) team fouls within each half, the opposing team is awarded two (2) free throws as a penalty.']),
      section('TEAM SIZE', ['The team roster in has a maximum limit of 12 players.', 'Players must participate in a minimum of three (3) regular-season games in order to be eligible for playoff.']),
      section('TECHNICAL FAULT', ['In the event of a technical or unsportsmanlike foul, the opposing team will be awarded two points and possession of the ball.', 'After accumulating two (2) technical fouls, the player will be ejected from the game.']),
      section('ZERO TOLERATION', ['Fighting, harassment, racial discrimination, or verbal abuse.', 'Immediate ejection for offenders.', 'Ejected players face a 1-game suspension, with the possibility of more severe consequences.', 'No fighting allowed; three (3) game suspension.', 'Ejections for punches or unsportmanlike contact, even if detected later through review.']),
      section('PAYMENT', ['It is mandatory for teams to settle their registration fees before the commencement of Game 1.', 'After payment is processed, it is important to be aware that we maintain a firm no-refund policy, and all sales are considered final without exceptions or refunds.', `EMT (Email Money Transfer) to ${supportContact}.`]),
      section('LATE/FORFEIT', ['Each team must have at least 4 players present inside the gym within 15 minutes after the scheduled game time.', 'If a team does not meet this requirement, the opposing team will be awarded 2 points for every additional minute that passes.', 'If more than 25 minutes have elapsed past the scheduled start time and the team still cannot field 4 players, that team will forfeit the game.', 'This rule applies to both regular-season games and playoffs.', `The team is required to pay a fine of $150 by Email Money Transfer to ${supportContact} before their next scheduled game.`, `Note: To avoid the penalty, teams must inform the league in advance by contacting ${supportContact} by Thursday midnight if they know they will be unable to field a team for a game.`]),
      section('TEAM NAME', ['Players do not need to be (by blood) the country they would like to play for or represent.', 'Teams must register with the official country name recognized by international bodies.']),
      section('TIEBREAKER', ['Tiebreakers are determined by point differential.']),
    ],
  };
}

export function worldLeagueRefundPolicy(leagueName = 'World League', supportEmail = 'info@worldleague.ca'): TenantRefundPolicy {
  const supportContact = compact(supportEmail) || 'league administration';
  return {
    configured: false,
    introduction: '',
    sections: [
      section('No Refunds After Registration Deadline', [], 'Refunds are not available after the registration cutoff date.'),
      section('Refund Amount', [], 'Original payment minus a $50 administrative fee (plus applicable taxes and processing fees).'),
      section('Why Refunds Are Limited', [
        'After the cutoff: No refunds are processed after Registration Deadline.',
        'Player-related: Injuries, changes in physical condition, personal schedule changes, work/academic conflicts, relocation, transportation issues, or missed games for any reason.',
        'Discipline: Suspensions, ejections, or removal for any Player Code of Conduct violation (e.g., fighting, threats, abusive or disruptive behavior).',
        'Game operations: Weather-related closures, opponent forfeits, teammate no-shows, referee no-shows, canceled or rescheduled games, changes to facilities, game times, or season format.',
        'Facilities & third parties: Venue policy changes or loss of facility contracts due to player/team misconduct (e.g., fighting, drinking, damage).',
        'Events beyond our control: Public health emergencies (e.g., COVID-19), government orders or restrictions, power outages, labor actions, or other force-majeure events.',
      ], `Upon registration and payment, funds are immediately allocated to league expenses such as jerseys, facility rentals, officials, staffing, and administration. Therefore, ${leagueName} does not provide refunds under the circumstances listed below.`),
      section('Refund Procedure', [`Please email ${supportContact} for refund requests.`, 'Approved refunds will be processed within 5-10 business days.']),
      section('PLEASE NOTE', [], `This refund policy is subject to change at the discretion of ${leagueName}. Refund decisions will be made in accordance with this policy and our commitment to fairness and transparency. By clicking "I AGREE" during registration, you acknowledge that you have read, understood, and agreed to the terms and conditions of this refund policy.`, 'notice'),
      section(`Team Enrollment Policy - ${leagueName}`, [], `The Team Enrollment Policy ensures that all ${leagueName} teams are registered fairly, consistently, and in alignment with our Code of Conduct. This policy outlines the requirements and responsibilities associated with forming a team and enrolling players for each season.`),
      section('Team Registration', ['Teams must register by the deadlines set for each season.', 'A team is not considered confirmed until full payment of the team fee is received.', 'The team fee covers up to 12 players on a roster.', 'Any additional players beyond the first 12 must register individually and pay the individual registration fee.']),
      section('Roster Requirements', [`All players must be officially registered through the ${leagueName} platform before participating in any game.`, 'Captains are responsible for ensuring all rostered athletes have completed registration and are eligible to play.', 'The League may request roster verification at any time to ensure compliance.']),
      section('Captain Responsibilities', ['Communicate all League rules, schedules, and updates to their team.', 'Confirm that each player is properly enrolled and has agreed to the Athlete Code of Conduct.', 'Ensure any additional players who join after the initial roster limit register individually.'], 'Captains play a leadership role in maintaining a safe and respectful environment. In addition to the expectations outlined in the Athlete Code of Conduct, captains must:'),
      section('Roster Changes', ['Roster additions or substitutions must be submitted through the official registration system.', 'The League may impose deadlines for adding new players to ensure fair competition and administrative accuracy.', 'Unauthorized or improperly registered players will not be permitted to play.']),
      section('Division Placement & Competitive Integrity', [], `${leagueName} does not guarantee that teams will be placed in the division they select during registration. Final division placement is determined solely by League Operations. We reserve the right to move any team up or down a division at any point before the season begins and during the season to preserve competitive balance, ensure fair matchups, and maintain the integrity of the league. All placement decisions made by the League are final.`),
      section('Payment & Refunds', ['All fees must be paid in full at the time of registration.', `Refunds are handled according to ${leagueName}'s Refund Policy.`, "Disciplinary removals, ejections, or suspensions will follow the Refund Policy's guidelines regarding eligibility for refunds."]),
    ],
  };
}

export function hasRulesPolicyContent(policy: TenantRulesPolicy): boolean {
  return Boolean(compact(policy.introduction) || policy.show_fiba_resource === true || policy.sections?.length);
}

export function hasRefundPolicyContent(policy: TenantRefundPolicy): boolean {
  return Boolean(compact(policy.introduction) || policy.sections?.length);
}

export function cloneRulesPolicy(policy: TenantRulesPolicy): TenantRulesPolicy {
  return structuredClone(policy);
}

export function cloneRefundPolicy(policy: TenantRefundPolicy): TenantRefundPolicy {
  return structuredClone(policy);
}

function safeRulesPolicy(value: TenantRulesPolicy | undefined): TenantRulesPolicy {
  return value && Array.isArray(value.sections) ? cloneRulesPolicy(value) : emptyRulesPolicy();
}

function safeRefundPolicy(value: TenantRefundPolicy | undefined): TenantRefundPolicy {
  return value && Array.isArray(value.sections) ? cloneRefundPolicy(value) : emptyRefundPolicy();
}

export function resolveRulesPolicy(tenant: TenantConfig | null, leagueName: string): TenantPolicyView<TenantRulesPolicy> {
  const configured = safeRulesPolicy(tenant?.policies?.rules);
  const supportEmail = safeSupportEmail(tenant?.contact?.support_email);
  const safeMailto = supportMailto(supportEmail);
  const unavailableMessage = `Rules and regulations have not been published for ${leagueName}. Contact ${supportEmail || 'league administration'} for current information.`;
  if (configured.configured === true) {
    return { mode: hasRulesPolicyContent(configured) ? 'configured' : 'unavailable', document: configured, unavailableMessage, supportEmail, supportMailto: safeMailto };
  }
  if (compact(tenant?.slug).toLowerCase() === 'world-league') {
    return { mode: 'legacy', document: worldLeagueRulesPolicy(leagueName, supportEmail || 'info@worldleague.ca'), unavailableMessage, supportEmail, supportMailto: safeMailto };
  }
  return { mode: 'unavailable', document: emptyRulesPolicy(), unavailableMessage, supportEmail, supportMailto: safeMailto };
}

export function resolveRefundPolicy(tenant: TenantConfig | null, leagueName: string): TenantPolicyView<TenantRefundPolicy> {
  const configured = safeRefundPolicy(tenant?.policies?.refund);
  const supportEmail = safeSupportEmail(tenant?.contact?.support_email);
  const safeMailto = supportMailto(supportEmail);
  const unavailableMessage = `The refund policy has not been published for ${leagueName}. Contact ${supportEmail || 'league administration'} for current information.`;
  if (configured.configured === true) {
    return { mode: hasRefundPolicyContent(configured) ? 'configured' : 'unavailable', document: configured, unavailableMessage, supportEmail, supportMailto: safeMailto };
  }
  if (compact(tenant?.slug).toLowerCase() === 'world-league') {
    return { mode: 'legacy', document: worldLeagueRefundPolicy(leagueName, supportEmail || 'info@worldleague.ca'), unavailableMessage, supportEmail, supportMailto: safeMailto };
  }
  return { mode: 'unavailable', document: emptyRefundPolicy(), unavailableMessage, supportEmail, supportMailto: safeMailto };
}
